import { useState } from 'react';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { THRESHOLD_UNITS, Timezones } from 'infrastructure/enums';

import { getNursingInterventionValues } from '../helpers';
import { bradycardiaSymptoms, tachycardiaSymptoms } from '../templates/pulse';
import { generatePulseMessage } from '../blurbs';

import type { IUsePulseProps } from '../types';
import type { IPulseValues } from 'components/molecules/rpm-alert-assessment/types';

export const usePulseAssessment = ({
  type,
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUsePulseProps) => {
  const [openEmergencyModal, setOpenEmergencyModal] = useState<boolean>(false);
  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const initialValues: IPulseValues = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    bounds: null,
    repeatReadingWas: null,
    // Q5
    symptoms: null,
    confirmedSymptoms: [],
    deniedSymptoms: [],
    symptomsOnset: null,
    emergencyCare: null,
    checkSymptoms: true,
    // Q6
    medicationTiming: null,
    // Q7
    isTakeMedications: null,
    // Q8
    isTakePrnMedications: null,
    // Q9
    details: null,
    // Q10
    confirmedFactors: [],
    deniedFactors: [],
    // Q11
    escalationToClinic: [],
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    // Q12
    verbalized: null,
  };

  const symptomsList =
    type === 'min' ? bradycardiaSymptoms : tachycardiaSymptoms;

  const reading = `${values.pulse} ${THRESHOLD_UNITS.Pulse}`;
  const timeZone =
    patientInfo?.loginInfo?.timezone || Timezones['America/Chicago'];

  const dataInPatientTimeZone = convertDateToTimeZone(
    new Date(Number(activityDate)),
    Timezones[timeZone],
  );
  const dateOfReading = DateFormatter({
    date: dataInPatientTimeZone,
  });

  const emergentSymptoms = symptomsList
    .filter((item) => item.required)
    .map((item) => item.label);

  const preparedData = (data: IPulseValues) => {
    const lastDate = data.last
      ? DateFormatter({ date: new Date(data.last) })
      : null;

    const nextDate = data.next
      ? DateFormatter({ date: new Date(data.next) })
      : null;
    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );
    const confirmedEmergentSymptoms = data.confirmedSymptoms?.filter(
      (item: string) => emergentSymptoms.includes(item),
    );
    const confirmedSymptomsWithoutEmergent = data.confirmedSymptoms?.filter(
      (item: string) => !emergentSymptoms.includes(item),
    );
    const isSpokeWithPatient = data.patient === 'Patient';

    return {
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpokeWithPatient,
        speakWithOther: data.other,
      },
      Q2: {
        AOC: data.AOC,
      },
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      Q4: {
        isOneMoreReading: data.isOneMoreReading ?? null,
        bounds: data.bounds ?? null,
        repeatReadingWas: data.repeatReadingWas ?? null,
      },
      Q5: {
        confirmedSymptoms: confirmedSymptomsWithoutEmergent,
        deniedSymptoms: data.deniedSymptoms,
        isAllSymptomsDenied:
          data.deniedSymptoms?.length === symptomsList.length,
        confirmedEmergentSymptoms,
        symptomsOnset: data.symptomsOnset,
        emergencyModalAnswer: data.emergencyCare,
      },
      Q6: {
        medicationTiming: data.medicationTiming,
      },
      Q7: {
        isTakeMedications: data.isTakeMedications,
      },
      Q8: {
        isTakePrnMedications: data.isTakePrnMedications,
      },
      Q9: {
        details: data.details,
      },
      Q10: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      // Nursing Intervention
      Q11: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q12: {
        verbalized: data.verbalized ?? null,
      },
    };
  };

  const toggleEmergencyModal = () => setOpenEmergencyModal((prev) => !prev);

  const onSaveForm = (formData: IPulseValues) => {
    const preparedValues = preparedData(formData);
    const message = generatePulseMessage(preparedValues);

    handleSubmit(message);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    openEmergencyModal,
    symptomsList,
    onSaveForm,
    toggleEmergencyModal,
  };
};
